import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface CorrelationMatrixProps {
  correlationData?: Record<string, Record<string, number>>;
}

const CorrelationMatrix: React.FC<CorrelationMatrixProps> = ({ correlationData }) => {
  if (!correlationData || Object.keys(correlationData).length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading correlation data...</p>
        </div>
      </div>
    );
  }

  const getCorrelationColor = (correlation: number) => {
    const absCorr = Math.abs(correlation);
    if (absCorr >= 0.8) {
      return correlation > 0 ? 'bg-red-600' : 'bg-blue-600';
    } else if (absCorr >= 0.6) {
      return correlation > 0 ? 'bg-red-400' : 'bg-blue-400';
    } else if (absCorr >= 0.4) {
      return correlation > 0 ? 'bg-orange-400' : 'bg-indigo-400';
    } else if (absCorr >= 0.2) {
      return correlation > 0 ? 'bg-yellow-400' : 'bg-purple-400';
    } else {
      return 'bg-gray-300';
    }
  };

  const getCorrelationTextColor = (correlation: number) => {
    const absCorr = Math.abs(correlation);
    if (absCorr >= 0.4) {
      return 'text-white';
    }
    return 'text-gray-800';
  };

  const getCorrelationStrength = (correlation: number) => {
    const absCorr = Math.abs(correlation);
    if (absCorr >= 0.8) return 'Very Strong';
    if (absCorr >= 0.6) return 'Strong';
    if (absCorr >= 0.4) return 'Moderate';
    if (absCorr >= 0.2) return 'Weak';
    return 'Very Weak';
  };

  const formatCorrelation = (value: number | string | null | undefined) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;
    return numValue.toFixed(2);
  };

  // Get unique assets from correlation data
  const assets = Object.keys(correlationData);
  
  // Create complete correlation matrix (including self-correlations as 1.0)
  const completeMatrix: Record<string, Record<string, number>> = {};
  
  assets.forEach(asset1 => {
    completeMatrix[asset1] = {};
    assets.forEach(asset2 => {
      if (asset1 === asset2) {
        completeMatrix[asset1][asset2] = 1.0; // Perfect correlation with itself
      } else if (correlationData[asset1]?.[asset2] !== undefined) {
        completeMatrix[asset1][asset2] = correlationData[asset1][asset2];
      } else if (correlationData[asset2]?.[asset1] !== undefined) {
        completeMatrix[asset1][asset2] = correlationData[asset2][asset1];
      } else {
        completeMatrix[asset1][asset2] = 0.0; // Default if no data
      }
    });
  });

  // Find most/least correlated pairs
  const correlationPairs: Array<{ asset1: string; asset2: string; correlation: number }> = [];
  for (let i = 0; i < assets.length; i++) {
    for (let j = i + 1; j < assets.length; j++) {
      const correlation = completeMatrix[assets[i]][assets[j]];
      correlationPairs.push({
        asset1: assets[i],
        asset2: assets[j],
        correlation
      });
    }
  }
  
  correlationPairs.sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));

  return (
    <div className="space-y-6">
      {/* Correlation Matrix Heatmap */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900">Correlation Matrix</h4>
        
        {assets.length > 1 ? (
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <table className="table-fixed border-collapse">
                <thead>
                  <tr>
                    <th className="w-16 h-16"></th>
                    {assets.map((asset) => (
                      <th key={asset} className="w-16 h-16 text-xs font-medium text-gray-700 text-center">
                        <div className="transform -rotate-45 origin-center w-16">
                          {asset}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset1) => (
                    <tr key={asset1}>
                      <td className="w-16 h-16 text-xs font-medium text-gray-700 text-right pr-2">
                        {asset1}
                      </td>
                      {assets.map((asset2) => {
                        const correlation = completeMatrix[asset1][asset2];
                        return (
                          <td key={asset2} className="w-16 h-16 border border-gray-200">
                            <div
                              className={`w-full h-full flex items-center justify-center text-xs font-medium ${getCorrelationColor(correlation)} ${getCorrelationTextColor(correlation)}`}
                              title={`${asset1} vs ${asset2}: ${formatCorrelation(correlation)} (${getCorrelationStrength(correlation)})`}
                            >
                              {formatCorrelation(correlation)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-sm text-gray-500">
            Need at least 2 assets to show correlation matrix
          </div>
        )}
      </div>

      {/* Correlation Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-xs font-medium text-gray-700 mb-3">Correlation Strength Guide</h5>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          <div className="text-center">
            <div className="w-4 h-4 bg-red-600 rounded mx-auto mb-1"></div>
            <div className="text-gray-600">Very Strong</div>
            <div className="text-gray-500">+0.8 to +1.0</div>
          </div>
          <div className="text-center">
            <div className="w-4 h-4 bg-orange-400 rounded mx-auto mb-1"></div>
            <div className="text-gray-600">Strong</div>
            <div className="text-gray-500">+0.6 to +0.8</div>
          </div>
          <div className="text-center">
            <div className="w-4 h-4 bg-yellow-400 rounded mx-auto mb-1"></div>
            <div className="text-gray-600">Moderate</div>
            <div className="text-gray-500">+0.4 to +0.6</div>
          </div>
          <div className="text-center">
            <div className="w-4 h-4 bg-gray-300 rounded mx-auto mb-1"></div>
            <div className="text-gray-600">Weak</div>
            <div className="text-gray-500">+0.2 to +0.4</div>
          </div>
          <div className="text-center">
            <div className="w-4 h-4 bg-blue-600 rounded mx-auto mb-1"></div>
            <div className="text-gray-600">Negative</div>
            <div className="text-gray-500">-0.2 to -1.0</div>
          </div>
        </div>
      </div>

      {/* Top Correlations */}
      {correlationPairs.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Key Relationships</h4>
          <div className="space-y-2">
            {correlationPairs.slice(0, 5).map((pair, index) => (
              <div key={`${pair.asset1}-${pair.asset2}`} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-xs font-medium text-gray-800">
                    {index + 1}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">{pair.asset1}</span>
                    <span className="text-gray-500 mx-2">↔</span>
                    <span className="font-medium text-gray-900">{pair.asset2}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500">{getCorrelationStrength(pair.correlation)}</span>
                  <div
                    className={`px-2 py-1 rounded text-xs font-medium ${getCorrelationColor(pair.correlation)} ${getCorrelationTextColor(pair.correlation)}`}
                  >
                    {formatCorrelation(pair.correlation)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Diversification Insights */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
          <InformationCircleIcon className="h-4 w-4 mr-2" />
          Diversification Insights
        </h4>
        <ul className="space-y-1 text-xs text-blue-800">
          {correlationPairs.length > 0 && (
            <>
              {correlationPairs.some(p => Math.abs(p.correlation) > 0.7) && (
                <li>• High correlations detected - consider diversifying into less correlated assets</li>
              )}
              {correlationPairs.some(p => p.correlation < -0.3) && (
                <li>• Negative correlations found - excellent for portfolio diversification</li>
              )}
              {correlationPairs.filter(p => Math.abs(p.correlation) < 0.3).length > correlationPairs.length * 0.5 && (
                <li>• Good diversification - most assets show low correlation</li>
              )}
              {correlationPairs.every(p => Math.abs(p.correlation) < 0.8) && correlationPairs.length > 2 && (
                <li>• Balanced portfolio - no extreme correlations detected</li>
              )}
            </>
          )}
          <li>• Lower correlations generally improve risk-adjusted returns</li>
          <li>• Monitor correlations during market stress - they tend to increase</li>
        </ul>
      </div>

      {/* Summary Statistics */}
      {correlationPairs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {formatCorrelation(Math.max(...correlationPairs.map(p => p.correlation)))}
            </div>
            <div className="text-xs text-gray-500">Highest Correlation</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {formatCorrelation(Math.min(...correlationPairs.map(p => p.correlation)))}
            </div>
            <div className="text-xs text-gray-500">Lowest Correlation</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {formatCorrelation(
                correlationPairs.reduce((sum, p) => sum + p.correlation, 0) / correlationPairs.length
              )}
            </div>
            <div className="text-xs text-gray-500">Average Correlation</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {correlationPairs.filter(p => Math.abs(p.correlation) > 0.5).length}
            </div>
            <div className="text-xs text-gray-500">Strong Relationships</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrelationMatrix; 